import {
  SaveOutlined
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col,
  message,
  Row, Space, Spin, Typography
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import {
  Controller, FormProvider, useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Picker } from '../Picker';

import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import { createStaffFulltimeService, updateStaffFullnameService } from 'common/services/extends/staffFulltime';
import { StaffFullTimeDetailTypes, StaffFullTimeParamsTypes } from 'common/services/extends/staffFulltime/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectErrorByCode, formatDateYYYYMMDD } from 'common/utils/functions';
import { createFulltimeSchema, editFulltimeSchema } from 'common/utils/schemas';
import { TableDataTypes } from 'extends/EmployeeFullTime';

export type StaffFormReturnTypes = Omit<TableDataTypes, 'id'>;
export type CreateEmployeeFulltimeProps = {
  data?: StaffFullTimeDetailTypes;
  loading?: boolean;
} & ActiveRolesExpend;

type FormReturnTypes = Omit<TableDataTypes, 'id' | 'department'> & {
  userId?: OptionType;
  department?: Array<OptionType>;
};

const defaultValues: FormReturnTypes = {
  fullname: '',
  phoneNumber: '',
  idCardNumber: '',
  team: undefined,
  idCardIssuedAt: '',
  idCardIssuedBy: '',
  permanentAddress: '',
  dob: undefined,
  department: undefined,
  createdAt: '',
  updatedAt: '',
  userId: undefined
};

const CreateEmployeeFulltime: React.FC<CreateEmployeeFulltimeProps> = ({
  roleUpdate, loading = false, data
}) => {
  /* Hooks */
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));

  /* React-hook-form */
  const method = useForm<FormReturnTypes>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(idParams ? editFulltimeSchema : createFulltimeSchema)
  });

  const departmentStaffFulltimeOptions: OptionType[] = [
    {
      label: t('staffFulltime.account'),
      value: 'account',
    },
    {
      label: t('staffFulltime.operation'),
      value: 'operation',
    },
    {
      label: t('staffFulltime.ketoan'),
      value: 'ketoan',
    },
    {
      label: t('staffFulltime.sup'),
      value: 'sup',
    },
  ];

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['create-staff-fulltime'],
    createStaffFulltimeService,
    {
      onSuccess: () => {
        navigate(ROUTE_PATHS.EMPLOYEE_FULLTIME_MANAGEMENT);
        message.success(t('message.createSuccess'));
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['update-staff-fulltime'],
    updateStaffFullnameService,
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );
  /* Functions */
  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }

    const formData = method.getValues();

    const reqObj: StaffFullTimeParamsTypes = {
      department: formData.department?.map((d) => d.value?.toString() || ''),
      dob: formatDateYYYYMMDD(formData.dob),
      fullname: formData.fullname,
      idCardIssuedAt: formatDateYYYYMMDD(formData.idCardIssuedAt),
      idCardIssuedBy: formData.idCardIssuedBy || '',
      idCardNumber: formData.idCardNumber,
      permanentAddress: formData.permanentAddress || '',
      phoneNumber: formData.phoneNumber || '',
      team: formData.team,
      userId: formData.userId?.value ? Number(formData.userId?.value) : null
    };
    if (idParams) {
      updateMutate({
        id: idParams,
        data: reqObj
      });
    } else {
      createMutate(reqObj);
    }
  };

  useEffect(() => {
    if (data) {
      method.reset({
        fullname: data.staffFullTimeData?.fullname || '',
        idCardNumber: data.staffFullTimeData?.idCardNumber || '',
        idCardIssuedBy: data.staffFullTimeData?.idCardIssuedBy || '',
        permanentAddress: data.staffFullTimeData?.permanentAddress || '',
        dob: data.staffFullTimeData?.dob || '',
        idCardIssuedAt: data.staffFullTimeData?.idCardIssuedAt || '',
        phoneNumber: data.staffFullTimeData?.phoneNumber || '',
        department: data.staffFullTimeData?.department?.map((d) => ({
          label: d,
          value: d
        })),
        team: data.staffFullTimeData?.team,
        userId: data.userData ? {
          label: data.userData.name,
          value: data.userData.id
        } : undefined
      });
    }
  }, [data]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.employeeFullTime')}
        rightHeader={(
          <Space size={16}>
            <Button
              type="primary"
              disabled={(Boolean(idParams) && !roleUpdate)}
              onClick={method.handleSubmit(onSubmit)}
            >
              <SaveOutlined />
              {t('system.save')}
            </Button>
          </Space>
        )}
      />
      <Spin size="large" spinning={loading || updateLoading || createLoading}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Card type="inner">
              <div className="site-card-border-less-wrapper">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Row gutter={16}>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.identityNumber')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="idCardNumber"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Space className="idCardNumberInput" style={{ width: '100%', alignItems: 'flex-start' }}>
                            <Input
                              className="u-mt-8"
                              value={value}
                              onChange={(e) => {
                                onChange(e.target.value);
                              }}
                              error={error?.message}
                              size="large"
                              disabled={!!idParams}
                            />
                          </Space>
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.grantDate')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="idCardIssuedAt"
                        control={method.control}
                        render={({ field, fieldState: { error } }) => (
                          <Picker
                            className="u-mt-8"
                            size="large"
                            value={field.value ? dayjs(field.value) : undefined}
                            onChange={field.onChange}
                            format="DD/MM/YYYY"
                            style={{ width: '100%' }}
                            error={error?.message}
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.grantPlace')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="idCardIssuedBy"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('assignmentStaff.fullname')}
                        {' '}
                      </Typography.Text>
                      <Typography.Text strong type="danger">
                        *
                      </Typography.Text>
                      <Controller
                        name="fullname"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.dob')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="dob"
                        control={method.control}
                        render={({ field, fieldState: { error } }) => (
                          <Picker
                            className="u-mt-8"
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                            value={field.value ? dayjs(field.value) : undefined}
                            onChange={field.onChange}
                            format="DD/MM/YYYY"
                            style={{ width: '100%' }}
                            error={error?.message}
                            disableDateUnder18age
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('system.phone')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="phoneNumber"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.address')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="permanentAddress"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.team')}
                        {' '}
                        {!idParams && <Typography.Text strong type="danger">*</Typography.Text>}
                      </Typography.Text>
                      <Controller
                        name="team"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={12} xl={8} className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.department')}
                        {' '}
                      </Typography.Text>
                      <Typography.Text strong type="danger">
                        *
                      </Typography.Text>
                      <Controller
                        name="department"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <DropdownElement
                            options={departmentStaffFulltimeOptions}
                            locale="vi"
                            placeholder={t('staffFulltime.department')}
                            value={value}
                            isShowSearch
                            isGetOption
                            error={error?.message}
                            onChange={onChange}
                            size="large"
                            multiple={{
                              allowClear: true,
                            }}
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                    <Col className="u-mb-8">
                      <Typography.Text strong>
                        {t('staffFulltime.accountSystem')}
                        {' '}
                      </Typography.Text>
                      <Controller
                        name="userId"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <DropdownElement
                            locale="vi"
                            type="user"
                            placeholder={t('staffFulltime.accountSystem')}
                            isGetOption
                            value={value}
                            isShowSearch
                            error={error?.message}
                            onChange={onChange}
                            size="large"
                            disabled={(Boolean(idParams) && !roleUpdate)}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                </Space>
              </div>
            </Card>
          </FormProvider>
        </div>
      </Spin>
    </>
  );
};

export default CreateEmployeeFulltime;
